import { useState, useEffect, useMemo } from "react";
import { Loader2, X, ArrowRight, AlertTriangle, Check, Search, CreditCard, Banknote } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

// Auth header for the public /outfits/{id} endpoint (no auth needed but harmless)
const BASE_ACTIONS = [
  { key: "no_adjustment",          label: "No adjustment required",        tone: "neutral", group: "even" },
  { key: "additional_paid_manual", label: "Extra collected manually",      tone: "positive", group: "extra" },
  { key: "additional_pending",     label: "Extra pending (customer owes)", tone: "warn", group: "extra" },
  { key: "refund_processed_manual",label: "Refund processed manually",     tone: "positive", group: "refund" },
  { key: "refund_due",             label: "Refund due (not yet issued)",   tone: "warn", group: "refund" },
  { key: "waived",                 label: "Waived by admin",               tone: "neutral", group: "both" },
];
const STRIPE_CHARGE = { key: "stripe_charge", label: "Charge via Stripe (saved card)", tone: "stripe", group: "extra" };
const STRIPE_REFUND = { key: "stripe_refund", label: "Refund via Stripe (original card)", tone: "stripe", group: "refund" };

export function BookingExchangeModal({ booking, onClose, onExchanged }) {
  const [step, setStep] = useState("select"); // select | variation | review | confirm-stripe
  const [outfits, setOutfits] = useState([]);
  const [outfitQ, setOutfitQ] = useState("");
  const [loadingOutfits, setLoadingOutfits] = useState(true);

  const [newOutfitId, setNewOutfitId] = useState("");
  const [newOutfit, setNewOutfit] = useState(null); // full outfit doc when variations exist
  const [rentalOptionId, setRentalOptionId] = useState("");
  const [variationLoading, setVariationLoading] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const [paymentAction, setPaymentAction] = useState("no_adjustment");
  const [paymentNote, setPaymentNote] = useState("");
  const [pendingAmount, setPendingAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showStripeConfirm, setShowStripeConfirm] = useState(false);

  const idempotencyKey = useMemo(() => `exch_${booking.id}_${Date.now()}_${Math.random().toString(36).slice(2,8)}`, [booking.id]);

  // Load outfits list once
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/outfits?available_only=true`);
        const data = Array.isArray(r.data) ? r.data : (r.data?.outfits || []);
        setOutfits(data);
      } catch {}
      setLoadingOutfits(false);
    })();
  }, []);

  const filteredOutfits = useMemo(() => {
    const q = outfitQ.trim().toLowerCase();
    return outfits
      .filter((o) => !o.archived)
      .filter((o) => !q ||
        (o.name || "").toLowerCase().includes(q) ||
        (o.category || "").toLowerCase().includes(q) ||
        (o.sku || "").toLowerCase().includes(q))
      .slice(0, 300);
  }, [outfits, outfitQ]);

  // Pick the right price tier given the booking's rental_days
  const priceFor = (source) => {
    const days = booking.rental_days || 4;
    const p4 = Number(source?.price_4_day || 0);
    const p8 = Number(source?.price_8_day || 0);
    const pd = Number(source?.price_per_day || 0);
    if (days <= 4 && p4 > 0) return p4;
    if (days > 4 && p8 > 0) return p8;
    return p4 || pd || 0;
  };

  // Pick outfit — fetch full doc, then either go to variations step or straight to review
  const handlePickOutfit = async (outfit) => {
    setNewOutfitId(outfit.id);
    setRentalOptionId("");
    setPreview(null);
    setPreviewError("");
    setVariationLoading(true);
    try {
      // Pull full outfit for rental_options
      const r = await axios.get(`${API}/outfits/${outfit.id}`);
      const full = r.data;
      setNewOutfit(full);
      const variations = Array.isArray(full.rental_options) ? full.rental_options : [];
      if (variations.length > 0) {
        setStep("variation");
      } else {
        setStep("review");
        loadPreview(outfit.id, "");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load outfit details");
    }
    setVariationLoading(false);
  };

  const handlePickVariation = (variationId) => {
    setRentalOptionId(variationId);
    setStep("review");
    loadPreview(newOutfitId, variationId);
  };

  const loadPreview = async (outfitId, varId) => {
    setPreviewing(true);
    setPreviewError("");
    try {
      const url = `${API}/admin/bookings/${booking.id}/exchange-preview?new_outfit_id=${outfitId}` +
                  (varId ? `&rental_option_id=${encodeURIComponent(varId)}` : "");
      const r = await axios.get(url, { headers: auth(), timeout: 25000 });
      if (r.data?.multi_cart) {
        setPreview({ multi_cart: true, message: r.data.message });
        return;
      }
      setPreview(r.data);
      // Suggest a payment action based on delta + stripe availability
      const delta = r.data?.price_delta?.delta || 0;
      const caps = r.data?.stripe || {};
      if (delta > 0) {
        setPaymentAction(caps.can_charge ? "stripe_charge" : "additional_pending");
      } else if (delta < 0) {
        setPaymentAction(caps.can_refund ? "stripe_refund" : "refund_due");
      } else {
        setPaymentAction("no_adjustment");
      }
      setPendingAmount(Math.abs(delta));
    } catch (err) {
      const status = err.response?.status;
      const detailRaw = err.response?.data?.detail;
      const detail = typeof detailRaw === "string" ? detailRaw : (detailRaw?.message || "");
      let msg;
      if (status === 404) msg = "Endpoint not found (404). The backend may not have been redeployed with Batch 4 — verify routes/booking_changes.py is registered in server.py and that the backend restarted.";
      else if (status === 401) msg = "Not authorised (401). Try logging out and back in as admin.";
      else if (err.code === "ECONNABORTED") msg = "Preview timed out after 25s.";
      else if (status) msg = `Preview failed (HTTP ${status})${detail ? ": " + detail : ""}`;
      else msg = `Could not reach backend${detail ? ": " + detail : ""}`;
      // eslint-disable-next-line no-console
      console.error("[BookingExchangeModal] preview failed:", { status, detail: detailRaw, err });
      setPreviewError(msg);
      toast.error(msg);
    }
    setPreviewing(false);
  };

  const submitExchange = async (acceptMissingPricing = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/bookings/${booking.id}/exchange`, {
        new_outfit_id: newOutfitId,
        rental_option_id: rentalOptionId,
        payment_action: paymentAction,
        payment_note: paymentNote,
        pending_amount: pendingAmount,
        accept_missing_pricing: acceptMissingPricing,
        idempotency_key: idempotencyKey,
      }, { headers: auth(), timeout: 40000 });
      const stripePI = r.data?.stripe_payment_intent_id;
      const stripeRf = r.data?.stripe_refund_id;
      if (stripePI) toast.success(`Charged $${Math.abs(r.data.price_delta).toFixed(2)} via Stripe — ${stripePI.slice(0, 14)}…`);
      else if (stripeRf) toast.success(`Refunded $${Math.abs(r.data.price_delta).toFixed(2)} via Stripe — ${stripeRf.slice(0, 14)}…`);
      else toast.success(`Outfit swapped to "${preview.replacement.outfit_name}"`);
      onExchanged?.(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object") {
        if (detail.code === "missing_replacement_pricing") {
          if (window.confirm(`${detail.message}\n\nProceed anyway with $0 rental?`)) {
            setSubmitting(false);
            return submitExchange(true);
          }
        } else if (detail.code === "stripe_card_declined") {
          toast.error(`Stripe declined: ${detail.message}`);
        } else if (detail.code?.startsWith("stripe_")) {
          toast.error(`${detail.code}: ${detail.message}`);
        } else if (detail.conflict_booking_id) {
          toast.error(`Conflict — outfit booked by ${detail.conflict_booking_id}`);
        } else {
          toast.error(detail.message || JSON.stringify(detail));
        }
      } else {
        toast.error(detail || "Exchange failed");
      }
    }
    setSubmitting(false);
    setShowStripeConfirm(false);
  };

  const handleConfirmClick = () => {
    if (paymentAction === "stripe_charge" || paymentAction === "stripe_refund") {
      setShowStripeConfirm(true);
    } else {
      submitExchange(false);
    }
  };

  // Build payment action list depending on delta + stripe caps
  const visibleActions = useMemo(() => {
    if (!preview?.price_delta) return [];
    const delta = preview.price_delta.delta;
    const caps = preview.stripe || {};
    const list = [];
    if (delta > 0) {
      if (caps.can_charge) list.push(STRIPE_CHARGE);
      list.push(...BASE_ACTIONS.filter((a) => a.group === "extra" || a.group === "both"));
    } else if (delta < 0) {
      if (caps.can_refund) list.push(STRIPE_REFUND);
      list.push(...BASE_ACTIONS.filter((a) => a.group === "refund" || a.group === "both"));
    } else {
      list.push(...BASE_ACTIONS.filter((a) => a.group === "even" || a.group === "both"));
    }
    return list;
  }, [preview]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="exchange-modal">
      <div className="bg-white max-w-2xl w-full p-6 border border-[#282828]/10 max-h-[92vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-[20px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              Exchange / Change Products
            </h3>
            <p className="text-[11px] text-[#676664]/60 mt-1">
              Booking {booking.id} · {booking.rental_days || 4}-day {booking.delivery_method || "rental"} ·
              Current outfit: <strong>{booking.outfit_name}</strong>
              {booking.rental_option_name && <> ({booking.rental_option_name})</>}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-[#676664]/40 hover:text-[#282828]" data-testid="close-exchange-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Original booking conditions banner — preserved during exchange */}
        <div className="bg-[#FAF8F0] border border-[#9A7B4F]/15 p-3 mb-4 text-[10px] text-[#676664]">
          <div className="text-[9px] uppercase tracking-wider text-[#9A7B4F] mb-1">Booking conditions preserved during exchange</div>
          <span>Event: <strong>{booking.event_date || "—"}</strong></span> ·
          <span> Return by: <strong>{booking.return_by_date || "—"}</strong></span> ·
          <span> Duration: <strong>{booking.rental_days || 4} days</strong></span> ·
          <span> Method: <strong>{booking.delivery_method || "—"}</strong></span> ·
          <span> Delivery fee: <strong>${Number(booking.delivery_fee || 0).toFixed(2)}</strong></span> ·
          <span> Discount: <strong>${Number(booking.discount_amount || 0).toFixed(2)}</strong></span>
        </div>

        {preview?.multi_cart && (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-4" data-testid="multi-cart-warning">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <div>
                <div className="text-[13px] text-amber-900 font-medium">Multi-item cart detected</div>
                <p className="text-[11px] text-amber-700 mt-1">{preview.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* STEP 1 — pick outfit */}
        {step === "select" && (
          <div data-testid="exchange-step-select">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/40" />
              <Input value={outfitQ} onChange={(e) => setOutfitQ(e.target.value)} placeholder="Search outfits by name/category/SKU…" className="pl-10 text-[13px]" data-testid="exchange-search" />
            </div>
            {loadingOutfits || variationLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
            ) : (
              <div className="max-h-80 overflow-y-auto divide-y divide-[#282828]/5 border border-[#282828]/8">
                {filteredOutfits.map((o) => {
                  const variations = Array.isArray(o.rental_options) ? o.rental_options.length : 0;
                  const price = priceFor(o);
                  return (
                    <button key={o.id} onClick={() => handlePickOutfit(o)}
                      className="w-full text-left px-3 py-2 hover:bg-[#FAF8F0]/60 flex items-center justify-between"
                      data-testid={`exchange-pick-${o.id}`}>
                      <div>
                        <div className="text-[13px] text-[#282828]">
                          {o.name}
                          {variations > 0 && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#9A7B4F]">{variations} variation{variations>1?"s":""}</span>}
                        </div>
                        <div className="text-[10px] text-[#676664]/50">
                          ${price.toFixed(2)} / {booking.rental_days || 4}-day · {o.category}
                          {o.sku && <span className="ml-1">· {o.sku}</span>}
                          {price === 0 && <span className="ml-2 text-amber-700">⚠ no price for this duration</span>}
                        </div>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-[#9A7B4F]" />
                    </button>
                  );
                })}
                {filteredOutfits.length === 0 && <div className="px-3 py-8 text-center text-[12px] text-[#676664]/50">No outfits match.</div>}
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — pick variation */}
        {step === "variation" && newOutfit && (
          <div data-testid="exchange-step-variation">
            <button onClick={() => { setStep("select"); setNewOutfit(null); }}
              className="text-[11px] text-[#676664]/60 hover:text-[#282828] uppercase tracking-wider mb-3"
              data-testid="variation-back-btn">← Back to outfits</button>
            <div className="bg-[#FAF8F0] p-3 mb-3 border border-[#282828]/8">
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Pick a variation for {newOutfit.name}</div>
              <div className="text-[10px] text-[#676664]/50">Pricing shown is for this booking's {booking.rental_days || 4}-day duration.</div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-[#282828]/5 border border-[#282828]/8">
              {(newOutfit.rental_options || []).map((opt) => {
                const optPrice = priceFor(opt) || priceFor(newOutfit);
                return (
                  <button key={opt.id} onClick={() => handlePickVariation(opt.id)}
                    className="w-full text-left px-3 py-2 hover:bg-[#FAF8F0]/60 flex items-center justify-between"
                    data-testid={`exchange-pick-variation-${opt.id}`}>
                    <div>
                      <div className="text-[13px] text-[#282828]">
                        {newOutfit.name} — {opt.name || opt.description || "Option"}
                      </div>
                      <div className="text-[10px] text-[#676664]/50">
                        {opt.size && <>Size {opt.size} · </>}
                        {opt.color && <>{opt.color} · </>}
                        {opt.sku && <>{opt.sku} · </>}
                        ${optPrice.toFixed(2)} / {booking.rental_days || 4}-day
                        {optPrice === 0 && <span className="ml-2 text-amber-700">⚠ no price</span>}
                      </div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-[#9A7B4F]" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3 — review preview */}
        {step === "review" && (
          <div data-testid="exchange-step-review">
            {previewError ? (
              <div className="py-6" data-testid="exchange-preview-error">
                <div className="bg-red-50 border border-red-200 p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                    <div>
                      <div className="text-[13px] text-red-900 font-medium">Couldn't load exchange preview</div>
                      <p className="text-[11px] text-red-700 mt-1 whitespace-pre-wrap">{previewError}</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <button onClick={() => { setStep("select"); setPreviewError(""); }} className="text-[11px] text-[#676664]/60 hover:text-[#282828] uppercase tracking-wider">← Pick different outfit</button>
                  <Button onClick={() => loadPreview(newOutfitId, rentalOptionId)} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white">Retry</Button>
                </div>
              </div>
            ) : previewing || !preview ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" />
                <p className="text-[11px] text-[#676664]/60 uppercase tracking-wider">Checking availability & pricing…</p>
              </div>
            ) : (
              <>
                {/* Missing-pricing warning */}
                {preview.replacement?.pricing_warning && (
                  <div className="bg-amber-50 border border-amber-200 p-3 mb-4" data-testid="missing-price-warn">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <p className="text-[11px] text-amber-800">{preview.replacement.pricing_warning}</p>
                    </div>
                  </div>
                )}

                {/* Comparison */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <OutfitCard label="Current" o={preview.current} />
                  <OutfitCard label="Replacement" o={preview.replacement} highlight variation={preview.replacement.variation_name} />
                </div>

                {/* Availability */}
                <div className={`p-3 mb-5 border ${preview.available ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="exchange-availability">
                  {preview.available ? (
                    <div className="flex items-center gap-1.5 text-[12px]"><Check className="w-3.5 h-3.5" /> Replacement is available for booking dates {preview.preserved.event_date} → {preview.preserved.return_by_date || "(return TBC)"}.</div>
                  ) : (
                    <div className="text-[12px]">
                      <div className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Unavailable for these dates.</div>
                      {preview.conflict && <div className="text-[10px] mt-1">Conflict with booking {preview.conflict.id} ({preview.conflict.event_date}).</div>}
                    </div>
                  )}
                </div>

                {/* Price summary */}
                <div className="bg-[#FAF8F0] p-4 border border-[#282828]/8 mb-5">
                  <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2">Price summary (same dates, duration, delivery fee, discount)</div>
                  <PriceRow label="Old rental" left={preview.current.rental_cost} />
                  <PriceRow label="New rental" left={preview.replacement.rental_cost} />
                  <PriceRow label="Bond" left={preview.current.deposit_amount} right={preview.replacement.deposit_amount} compare />
                  {preview.current.delivery_fee > 0 && <PriceRow label="Delivery fee (unchanged)" left={preview.current.delivery_fee} />}
                  {preview.current.discount_amount > 0 && <PriceRow label={`Discount (unchanged)`} left={-preview.current.discount_amount} />}
                  <div className="flex justify-between text-[12px] mt-1 pt-2 border-t border-[#282828]/8"><span>Old total</span><span>${preview.price_delta.old_total.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[12px]"><span>New total</span><span>${preview.price_delta.new_total.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[13px] mt-1 pt-2 border-t border-[#282828]/8 font-semibold">
                    <span>Difference</span>
                    <span className={preview.price_delta.delta > 0 ? "text-amber-700" : preview.price_delta.delta < 0 ? "text-emerald-700" : "text-[#282828]"}>
                      {preview.price_delta.delta > 0 ? "+" : ""}${preview.price_delta.delta.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Stripe capability banner */}
                {(preview.stripe?.can_charge || preview.stripe?.can_refund) && (
                  <div className="bg-blue-50 border border-blue-200 p-2.5 mb-4 text-[10px] text-blue-800 flex items-start gap-2">
                    <CreditCard className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                      This booking has saved Stripe data.
                      {preview.stripe.can_charge && " You may re-charge the customer's saved card off-session."}
                      {preview.stripe.can_refund && " You may refund to the original card."}
                    </div>
                  </div>
                )}

                {/* Payment action */}
                <div className="mb-4">
                  <label className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2 block">Payment / refund action</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {visibleActions.map((p) => (
                      <button key={p.key} onClick={() => setPaymentAction(p.key)}
                        className={`px-3 py-2 text-[11px] text-left border transition-colors inline-flex items-center gap-1.5 ${
                          paymentAction === p.key
                            ? p.tone === "stripe" ? "bg-blue-600 border-blue-700 text-white"
                              : p.tone === "positive" ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                              : p.tone === "warn" ? "bg-amber-50 border-amber-300 text-amber-900"
                              : "bg-[#282828] border-[#282828] text-white"
                            : "border-[#282828]/10 text-[#676664] hover:border-[#9A7B4F]/40"
                        }`}
                        data-testid={`payment-action-${p.key}`}>
                        {p.tone === "stripe" ? <CreditCard className="w-3 h-3" /> : <Banknote className="w-3 h-3" />}
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {(paymentAction === "additional_pending" || paymentAction === "refund_due") && (
                  <div className="mb-3">
                    <label className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1 block">Pending amount (AUD)</label>
                    <Input type="number" step="0.01" value={pendingAmount} onChange={(e) => setPendingAmount(parseFloat(e.target.value) || 0)} className="text-[13px]" />
                  </div>
                )}

                <div className="mb-5">
                  <label className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1 block">Internal note (optional)</label>
                  <textarea value={paymentNote} onChange={(e) => setPaymentNote(e.target.value)} rows={2} maxLength={500}
                    className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] focus:border-[#9A7B4F] focus:outline-none resize-none"
                    placeholder="Why this exchange / how it was paid?" />
                </div>

                <div className="flex justify-between items-center">
                  <button onClick={() => setStep(newOutfit?.rental_options?.length ? "variation" : "select")}
                    className="text-[11px] text-[#676664]/60 hover:text-[#282828] uppercase tracking-wider"
                    data-testid="exchange-back-btn">← Pick different outfit</button>
                  <Button onClick={handleConfirmClick} disabled={submitting || !preview.available}
                    className={`text-white disabled:opacity-40 ${
                      (paymentAction === "stripe_charge" || paymentAction === "stripe_refund")
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-emerald-700 hover:bg-emerald-800"
                    }`}
                    data-testid="confirm-exchange-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
                      (paymentAction === "stripe_charge" || paymentAction === "stripe_refund") ? <CreditCard className="w-4 h-4 mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    {paymentAction === "stripe_charge" ? "Review Stripe charge" :
                     paymentAction === "stripe_refund" ? "Review Stripe refund" : "Confirm exchange"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Stripe confirmation dialog (rendered on top) */}
      {showStripeConfirm && preview && (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" onClick={() => setShowStripeConfirm(false)}>
          <div className="bg-white max-w-md w-full p-6 border border-[#282828]/10" onClick={(e) => e.stopPropagation()} data-testid="stripe-confirm-dialog">
            <h4 className="text-[18px] mb-3 text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              Confirm Stripe {paymentAction === "stripe_charge" ? "charge" : "refund"}
            </h4>
            <div className="space-y-2 text-[12px] text-[#676664] mb-5">
              <div className="flex justify-between"><span>Customer:</span><span className="text-[#282828]">{booking.customer_name}</span></div>
              <div className="flex justify-between"><span>Email:</span><span className="text-[#282828]">{booking.customer_email}</span></div>
              <div className="flex justify-between"><span>Booking:</span><span className="text-[#282828] font-mono">{booking.id}</span></div>
              <div className="flex justify-between"><span>{paymentAction === "stripe_charge" ? "Charge amount" : "Refund amount"}:</span>
                <span className={`font-semibold text-[14px] ${paymentAction === "stripe_charge" ? "text-amber-700" : "text-emerald-700"}`}>
                  ${Math.abs(preview.price_delta.delta).toFixed(2)} AUD
                </span>
              </div>
              <div className="flex justify-between"><span>{paymentAction === "stripe_charge" ? "Saved card:" : "Original payment intent:"}</span>
                <span className="text-[#282828] font-mono text-[10px]">{paymentAction === "stripe_charge"
                  ? `${preview.stripe.stripe_payment_method_id?.slice(0, 14)}…`
                  : `${preview.stripe.stripe_payment_intent_id?.slice(0, 14)}…`}</span>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 p-3 mb-5 text-[10px] text-amber-800">
              {paymentAction === "stripe_charge"
                ? "This will charge the customer's saved card immediately. They consented to off-session re-charging at original checkout. The charge cannot be undone except by issuing a refund."
                : "This will refund to the original card via Stripe. Refunds typically appear in 5–10 business days."}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setShowStripeConfirm(false)} className="text-[11px]" data-testid="stripe-cancel-btn">Cancel</Button>
              <Button onClick={() => submitExchange(false)} disabled={submitting}
                className={`text-white ${paymentAction === "stripe_charge" ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-700 hover:bg-emerald-800"}`}
                data-testid="stripe-confirm-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                {paymentAction === "stripe_charge" ? `Charge $${Math.abs(preview.price_delta.delta).toFixed(2)}` : `Refund $${Math.abs(preview.price_delta.delta).toFixed(2)}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OutfitCard({ label, o, highlight, variation }) {
  return (
    <div className={`border p-3 ${highlight ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-[#282828]/10"}`}>
      <div className="text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">{label}</div>
      {o.image && <img src={o.image} alt="" className="w-full h-24 object-cover mb-2" />}
      <div className="text-[13px] text-[#282828] mb-1">
        {o.outfit_name || "—"}
        {variation && <span className="text-[10px] text-[#9A7B4F] ml-1">({variation})</span>}
      </div>
      <div className="text-[10px] text-[#676664]/60">Rental ${Number(o.rental_cost || 0).toFixed(2)}</div>
      <div className="text-[10px] text-[#676664]/60">Bond ${Number(o.deposit_amount || 0).toFixed(2)}</div>
    </div>
  );
}

function PriceRow({ label, left, right, compare }) {
  return (
    <div className="flex justify-between text-[12px]">
      <span>{label}</span>
      <span>
        {compare && right !== undefined
          ? <>${Number(left).toFixed(2)} <span className="text-[#676664]/40">→</span> ${Number(right).toFixed(2)}</>
          : <>${Number(left).toFixed(2)}</>}
      </span>
    </div>
  );
}
